import React, { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ShieldCheck, Loader2, ExternalLink } from "lucide-react";
import { getDisplayMode, isRestrictedUpiIntentContext } from "@/lib/paymentContext";
import { waitForRadixReleased } from "@/lib/dialogRelease";

const API = process.env.REACT_APP_BACKEND_URL;
const CHECKOUT_JS = "https://checkout.razorpay.com/v1/checkout.js";

function loadCheckoutJs() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("no-window"));
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${CHECKOUT_JS}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () =>
        reject(new Error("checkout-load-failed"))
      );
      return;
    }
    const s = document.createElement("script");
    s.src = CHECKOUT_JS;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("checkout-load-failed"));
    document.head.appendChild(s);
  });
}

/**
 * BailPaymentDialog — single-product ₹40 Bail Affidavit gate.
 * Props: open, onOpenChange, onPaid(download_token), userName, userEmail
 */
export function BailPaymentDialog({
  open,
  onOpenChange,
  onPaid,
  userName = "",
  userEmail = "",
}) {
  const [configured, setConfigured] = useState(null);
  const [pricePaise, setPricePaise] = useState(4000);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/payments/config`, {
          method: "POST",
          credentials: "include",
        });
        if (!r.ok) {
          if (!cancelled) setConfigured(false);
          return;
        }
        const j = await r.json();
        if (cancelled) return;
        setConfigured(!!j.configured);
        const p = j.product_prices_paise?.bail_affidavit;
        if (p) setPricePaise(p);
      } catch {
        if (!cancelled) setConfigured(false);
      }
    })();
    // Warm up Razorpay checkout.js the moment the dialog opens so the
    // Pay click path is as short and synchronous as possible — this
    // preserves Android Chrome user-activation for the eventual UPI
    // Intent navigation. loadCheckoutJs() is idempotent + memoized on
    // window.Razorpay so this is safe to call multiple times.
    loadCheckoutJs().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open]);

  const rupees = (pricePaise / 100).toFixed(0);

  const startPayment = async () => {
    if (configured === false) {
      toast.error("Payment gateway is not yet configured.");
      return;
    }
    setBusy(true);
    try {
      const orderRes = await fetch(`${API}/api/payments/create-order`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pdf_count: 1, product: "bail_affidavit" }),
      });
      if (orderRes.status === 503) {
        toast.error("Payment temporarily unavailable. Try again later.");
        return;
      }
      if (!orderRes.ok) throw new Error(`create-order-${orderRes.status}`);
      const order = await orderRes.json();

      await loadCheckoutJs();

      // Deterministic handover to Razorpay Checkout for reliable Android
      // UPI Intent launch:
      //   (a) flushSync forces React to commit the Radix Dialog close
      //       synchronously — no scheduler delays under load.
      //   (b) waitForRadixReleased polls (via rAF) until the Radix
      //       Dialog's aria-hidden / focus-trap are actually gone from
      //       the DOM — no fixed timeout, no device-dependent race.
      // Only after both signals resolve does rzp.open() run, giving
      // the Razorpay iframe a clean top-level user-gesture context
      // and letting `intent://` UPI-app navigations fire reliably.
      flushSync(() => onOpenChange(false));
      await waitForRadixReleased();

      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          order_id: order.order_id,
          name: "Advocate Document Studio",
          description: "Bail Affidavit PDF",
          prefill: { name: userName, email: userEmail },
          // Explicit method allow-list — matches Razorpay's own JS
          // reference for enabling UPI Intent on Android mobile web.
          // Card / Netbanking / Wallet remain enabled exactly as before.
          method: { upi: true, card: true, netbanking: true, wallet: true },
          // Helps Razorpay Support correlate any UPI Intent failure to
          // the exact browser context if the merchant raises a ticket.
          notes: { display_mode: getDisplayMode() },
          theme: { color: "#B08A45" },
          handler: async (rzpRes) => {
            try {
              const vr = await fetch(`${API}/api/payments/verify`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  razorpay_order_id: rzpRes.razorpay_order_id,
                  razorpay_payment_id: rzpRes.razorpay_payment_id,
                  razorpay_signature: rzpRes.razorpay_signature,
                  pdf_count: 1,
                }),
              });
              if (!vr.ok) throw new Error("verify-failed");
              const vj = await vr.json();
              if (!vj.verified || !vj.download_token)
                throw new Error("verify-not-ok");
              toast.success("Payment verified. Preparing PDF…");
              await onPaid(vj.download_token);
              resolve();
            } catch (err) {
              console.error(err);
              toast.error("Payment could not be verified.");
              reject(err);
            }
          },
          modal: {
            ondismiss: () => reject(new Error("cancelled")),
          },
        });
        rzp.on("payment.failed", () => {
          toast.error("Payment failed.");
          reject(new Error("failed"));
        });
        rzp.open();
      });
    } catch (err) {
      if (err?.message === "cancelled") toast.info("Payment cancelled.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]" data-testid="bail-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" style={{ color: "#B08A45" }} />
            Download Bail Affidavit
          </DialogTitle>
          <DialogDescription>
            Pay ₹{rupees} to generate a court-ready PDF of your Bail Affidavit.
          </DialogDescription>
        </DialogHeader>

        <div
          className="rounded-2xl p-4"
          style={{
            background: "linear-gradient(180deg, rgba(176,138,69,0.06), rgba(176,138,69,0.02))",
            boxShadow: "inset 0 0 0 1px rgba(176,138,69,0.20)",
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[13.5px] font-semibold text-stone-800">
                Bail Affidavit PDF
              </div>
              <div className="text-[12px] text-stone-500">
                Generated from the original court template.
              </div>
            </div>
            <div className="text-[18px] font-bold text-stone-900" data-testid="bail-total">
              ₹{rupees}
            </div>
          </div>
        </div>

        {configured === false ? (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              Payment gateway is not yet configured. Please try again later.
            </div>
          </div>
        ) : null}

        {isRestrictedUpiIntentContext() ? (
          <div
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[12.5px] text-amber-900"
            data-testid="bail-upi-standalone-notice"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              You are using the installed app mode. If Google Pay / PhonePe /
              Paytm does not open on tap, open this page in Chrome once — UPI
              apps will then launch normally.
              <button
                type="button"
                onClick={() => {
                  try {
                    window.open(window.location.href, "_blank", "noopener,noreferrer");
                  } catch (_) {
                    window.location.href = window.location.href;
                  }
                }}
                data-testid="bail-upi-open-in-browser"
                className="ml-1 inline-flex items-center gap-0.5 font-semibold text-amber-900 underline"
              >
                Open in Chrome <ExternalLink className="h-3 w-3" />
              </button>
            </div>
          </div>
        ) : null}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="bail-pay-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={startPayment}
            disabled={busy || configured === false}
            data-testid="bail-pay-btn"
            style={{ background: "#B08A45" }}
            className="text-white hover:opacity-90"
          >
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing…
              </>
            ) : (
              <>Pay ₹{rupees} &amp; Download</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
